/**
 * Geography > Lookup (WPS-222) - "what does this address resolve to?" A
 * generated form posts to the same `/geo/resolve/` the registration wizard
 * and claim routing use, and shows the chain: zone, plant service area,
 * resolution method + confidence. The fastest way for an admin to verify the
 * reference data they just edited.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { useForm, type FormField } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'
import { useGeoMeta, useGeoResolve, type GeoResolveResult } from '@/admin/hooks/use-geo-reference'

import { zoneLabel } from './geography-utils'

export function LookupSection() {
  const { t } = useTranslation()
  const { data: meta } = useGeoMeta()
  const resolve = useGeoResolve()
  const [result, setResult] = useState<GeoResolveResult | null>(null)

  const businessOptions = useMemo(() => meta?.businesses ?? [], [meta?.businesses])
  const countryOptions = useMemo(
    () => (meta?.country_codes?.length ? meta.country_codes : [{ value: 'US', label: 'US' }]),
    [meta?.country_codes],
  )
  // Default to the country most of the crosswalk is in (US when present), not
  // whichever code sorts first.
  const defaultCountry = useMemo(
    () => (countryOptions.some((o) => o.value === 'US') ? 'US' : countryOptions[0]?.value ?? 'US'),
    [countryOptions],
  )

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'postal_code',
        label: t('geography.lookup.field.postalCode', 'Postal code'),
        value: '',
        required: t('geography.lookup.postalCodeRequired', 'Enter a postal code'),
        testId: 'geography-lookup-postal',
      },
      {
        type: 'select',
        name: 'country_code',
        label: t('geography.lookup.field.country', 'Country'),
        value: defaultCountry,
        options: countryOptions,
      },
      {
        type: 'text',
        name: 'state',
        label: t('geography.lookup.field.state', 'State (optional)'),
        value: '',
      },
      ...(businessOptions.length
        ? [
            {
              type: 'select' as const,
              name: 'business',
              label: t('geography.lookup.field.business', 'Business line'),
              value: businessOptions[0].value,
              options: businessOptions,
            },
          ]
        : []),
    ],
    [businessOptions, countryOptions, defaultCountry, t],
  )

  const { Form } = useForm(
    { name: 'geography-lookup-form', submit: 'footer', fields },
    async (raw) => {
      const v = raw as Record<string, unknown>
      try {
        const state = String(v.state ?? '').trim()
        const res = await resolve.mutateAsync({
          postal_code: String(v.postal_code ?? '').trim(),
          country_code: String(v.country_code ?? 'US'),
          ...(state ? { state } : {}),
          ...(v.business ? { business: String(v.business) } : {}),
        })
        setResult(res)
      } catch (err) {
        toastApiError(err, t('geography.lookup.failed', 'Could not resolve that address.'))
      }
    },
    [fields],
  )

  return (
    <section data-testid="geography-lookup" className="flex flex-col gap-6">
      <div>
        <h2 className="clara-heading">{t('geography.lookup.title', 'Lookup')}</h2>
        <p className="mt-1 max-w-[720px] text-[13px] leading-relaxed text-[var(--ink-3)]">
          {t(
            'geography.lookup.description',
            'Check how an address resolves through the reference data: postal code to zone, zone to plant service area. The same chain registration and claim routing use.',
          )}
        </p>
      </div>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_1fr]">
        <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] p-5">
          <Form className="flex flex-col gap-4" />
          <div className="mt-4 flex justify-end">
            <Button
              type="submit"
              form="geography-lookup-form"
              disabled={resolve.isPending}
              data-testid="geography-lookup-submit"
            >
              <Search className="h-3.5 w-3.5" />
              {resolve.isPending
                ? t('geography.lookup.resolving', 'Resolving...')
                : t('geography.lookup.resolve', 'Resolve')}
            </Button>
          </div>
        </div>
        <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] p-5" data-testid="geography-lookup-result">
          {resolve.isPending ? (
            <p className="text-[13px] text-[var(--ink-3)]">{t('geography.lookup.resolving', 'Resolving...')}</p>
          ) : result === null ? (
            <p className="text-[13px] text-[var(--ink-3)]">
              {t('geography.lookup.prompt', 'Enter a postal code to see where it resolves.')}
            </p>
          ) : result.zone === null ? (
            <p className="text-[13px] text-[var(--ink)]">
              {t('geography.lookup.notFound', 'No zone resolves for that address. Check the postal-code crosswalk and the state default.')}
            </p>
          ) : (
            <dl className="grid grid-cols-[140px_1fr] gap-y-3 text-[13px]">
              <dt className="text-[var(--ink-3)]">{t('geography.lookup.result.zone', 'Zone')}</dt>
              <dd className="text-[var(--ink)]" data-testid="geography-lookup-zone">
                {zoneLabel(result.zone.name, result.zone.code)}
              </dd>
              <dt className="text-[var(--ink-3)]">{t('geography.lookup.result.psa', 'Plant service area')}</dt>
              <dd className="text-[var(--ink)]">
                {result.psa ? zoneLabel(result.psa.name, result.psa.code) : t('geography.lookup.result.noPsa', 'No PSA mapped for this business line')}
              </dd>
              <dt className="text-[var(--ink-3)]">{t('geography.lookup.result.method', 'Resolved via')}</dt>
              <dd className="flex items-center gap-2 text-[var(--ink)]">
                <span>{result.resolution_method || '-'}</span>
                {result.resolution_confidence ? (
                  <Badge variant="secondary">{result.resolution_confidence}</Badge>
                ) : null}
              </dd>
            </dl>
          )}
        </div>
      </div>
    </section>
  )
}
